import apiClient from './apiClient';

/**
 * Admin Proforma Invoice DTO
 *
 * Proforma data for admin list view (cross-tenant).
 */
export interface AdminProforma {
  id: string;
  proformaNumber: string;
  tenant: {
    id: string;
    name: string;
  };
  amountGross: number;
  currency: string;
  issueDate: string;
  dueDate: string;
  status: 'issued' | 'paid' | 'overdue';
  pdfUrl: string;
}

/**
 * Paginated response for admin proforma list
 */
export interface PaginatedProformasResponse {
  items: AdminProforma[];
  total: number;
  page: number;
  limit: number;
}

/**
 * Query parameters for listing proformas
 */
export interface ListProformasParams {
  search?: string;
  status?: 'issued' | 'paid' | 'overdue';
  page?: number;
  limit?: number;
}

/**
 * Response from mark-paid operation
 */
export interface MarkPaidResponse {
  success: boolean;
  message?: string;
  invoiceNumber?: string;
  newPeriodEnd?: string;
}

/**
 * Response from invoice import operation
 */
export interface ImportInvoicesResponse {
  imported: { vat: number; proforma: number };
  skipped: {
    alreadyExists: number;
    noMatchingTenant: number;
    noSubscription: number;
  };
  errors: { invoiceId: number; invoiceNumber: string; error: string }[];
  duration: string;
}

/**
 * Error thrown when admin billing API request fails
 */
export class AdminBillingApiError extends Error {
  constructor(
    message: string,
    public readonly statusCode?: number,
  ) {
    super(message);
    this.name = 'AdminBillingApiError';
  }
}

/**
 * Admin Billing API Service
 *
 * Handles API calls for admin proforma management (cross-tenant).
 *
 * @requires admin:kiedynabory module permission
 */
export const adminBillingApi = {
  /**
   * Get all proforma invoices (cross-tenant)
   *
   * @param params - Filter and pagination options
   * @returns Paginated list of proformas
   * @throws AdminBillingApiError with user-friendly message if request fails
   */
  getProformas: async (
    params: ListProformasParams = {},
  ): Promise<PaginatedProformasResponse> => {
    try {
      const response = await apiClient.get<PaginatedProformasResponse>(
        '/admin/proforma-invoices',
        { params },
      );
      return response.data;
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number } };
      const status = axiosError.response?.status;

      if (status === 401) {
        throw new AdminBillingApiError(
          'Sesja wygasła. Zaloguj się ponownie.',
          401,
        );
      }
      if (status === 403) {
        throw new AdminBillingApiError(
          'Brak uprawnień admin:kiedynabory.',
          403,
        );
      }
      if (status === 429) {
        throw new AdminBillingApiError(
          'Zbyt wiele żądań. Poczekaj chwilę i spróbuj ponownie.',
          429,
        );
      }
      throw new AdminBillingApiError(
        'Nie udało się załadować proform. Spróbuj ponownie.',
        status,
      );
    }
  },

  /**
   * Mark proforma as paid
   *
   * Creates billing operation: extends subscription, issues VAT invoice, sends email.
   * Idempotent - safe to call multiple times.
   *
   * @param proformaId - Proforma UUID
   * @returns Mark paid response with invoice number and new period end
   * @throws AdminBillingApiError with user-friendly message if request fails
   */
  markAsPaid: async (proformaId: string): Promise<MarkPaidResponse> => {
    try {
      const response = await apiClient.post<MarkPaidResponse>(
        `/admin/proforma-invoices/${proformaId}/mark-paid`,
      );
      return response.data;
    } catch (error: unknown) {
      const axiosError = error as {
        response?: { status?: number; data?: { message?: string } };
      };
      const status = axiosError.response?.status;
      const message = axiosError.response?.data?.message;

      if (status === 400) {
        throw new AdminBillingApiError(
          message || 'Nie można oznaczyć tej proformy jako opłaconej.',
          400,
        );
      }
      if (status === 401) {
        throw new AdminBillingApiError(
          'Sesja wygasła. Zaloguj się ponownie.',
          401,
        );
      }
      if (status === 403) {
        throw new AdminBillingApiError(
          'Brak uprawnień admin:kiedynabory.',
          403,
        );
      }
      if (status === 404) {
        throw new AdminBillingApiError('Proforma nie została znaleziona.', 404);
      }
      if (status === 429) {
        throw new AdminBillingApiError(
          'Zbyt wiele żądań. Poczekaj chwilę i spróbuj ponownie.',
          429,
        );
      }
      throw new AdminBillingApiError(
        'Nie udało się oznaczyć płatności. Spróbuj ponownie.',
        status,
      );
    }
  },

  /**
   * Import invoices from Fakturownia
   *
   * Fetches all VAT and proforma invoices from Fakturownia and imports them.
   * Matches invoices to tenants by NIP. Idempotent - safe to run multiple times.
   *
   * @param dryRun - If true, only simulate import without saving
   * @returns Import result with statistics
   * @throws AdminBillingApiError with user-friendly message if request fails
   */
  importInvoices: async (dryRun = false): Promise<ImportInvoicesResponse> => {
    try {
      const response = await apiClient.post<ImportInvoicesResponse>(
        '/admin/import-invoices',
        { dryRun },
      );
      return response.data;
    } catch (error: unknown) {
      const axiosError = error as {
        response?: { status?: number; data?: { message?: string } };
      };
      const status = axiosError.response?.status;
      const message = axiosError.response?.data?.message;

      if (status === 401) {
        throw new AdminBillingApiError(
          'Sesja wygasła. Zaloguj się ponownie.',
          401,
        );
      }
      if (status === 403) {
        throw new AdminBillingApiError(
          'Brak uprawnień administratora.',
          403,
        );
      }
      if (status === 429) {
        throw new AdminBillingApiError(
          'Import jest już w trakcie. Poczekaj 5 minut przed ponowną próbą.',
          429,
        );
      }
      throw new AdminBillingApiError(
        message || 'Nie udało się zaimportować faktur. Spróbuj ponownie.',
        status,
      );
    }
  },
};
